import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, of, throwError } from "rxjs";
import { ErrorMessage } from "../models/error-message.model";
import { IdentificationResult } from "../models/identification-result.model";
import { Tenancy } from "../models/tenancy.model";
import { Tenant } from "../models/tenant.model";
import { ViewModelService } from "./view-model.service";

/**
 * Identification Service for Identification & Verification Wizard
 *
 * This service handles all HTTP communication with the backend API for the identification wizard.
 * It manages the multi-step verification process:
 *
 * FLOW:
 * Step 1: getTenancy() - Validates tenancy reference + postcode, returns property details
 * Step 2: getTenants() - Gets list of tenants at the property
 * Step 3: getQuestions() - Retrieves available security questions for selected tenant
 * Step 4: postAnswers() - Submits answers to security questions for verification
 *
 */
@Injectable({
   providedIn: "root",
})
export class IdentificationService {
   private readonly accountApiUrl = "/api"; // Placeholder - update with actual URL

   public vm = inject(ViewModelService);
   private http = inject(HttpClient);

   /**
    * STEP 1: Validate Tenancy Reference and Postcode
    *
    * Purpose: Verifies that the tenancy reference and postcode combination exists in the system
    *
    * Called by: Step 1 component when user submits the initial form
    *
    * API Endpoint: GET /api/tenancy?tenancyReference={ref}&postcode={code}
    *
    * Request Parameters:
    * - tenancyReference: 4-6 digit tenancy reference number
    * - postcode: UK postcode format
    *
    * Response (200 OK): Tenancy object containing:
    * - addressLine1, addressLine2, addressLine3: Property address
    * - postcode: Property postcode
    * - assetId: Unique identifier for the property (IMPORTANT: used in subsequent API calls)
    * - allowPortalRegistration: Boolean - whether portal registration is allowed for this tenancy
    *
    * Response (204 No Content): Tenancy not found
    *
    * Behavior:
    * - On success: Stores tenancy in vm.tenancy, tracks GA event "Tenancy Found"
    * - On not found (204): Shows error message, tracks GA event "Tenancy Not Found"
    * - On error: Shows error message, returns undefined
    *
    * State Changes:
    * - Stores tenancy object in ViewModelService for use in subsequent steps
    * - The assetId from this response is critical for Steps 2 and 3
    */
   getTenancy(): Observable<Tenancy | undefined> {
      this.resetError();
      console.log("Getting tenancy for reference:", this.vm.tenancyReference, "and postcode:", this.vm.postcode);
      const url = `${this.accountApiUrl}/tenancy?tenancyReference=${this.vm.tenancyReference}&postcode=${this.vm.postcode}`;

      return this.http.get<Tenancy>(url).pipe(
         map((response: Tenancy) => {
            // Track successful lookup
            console.log("Tenancy found:", response);
            this.trackEvent("ID&V", "Step One", "Tenancy & Post Code - Found");

            // Store tenancy in view model for use in subsequent steps
            this.vm.tenancy = response;
            return this.vm.tenancy;
         }),
         catchError((error: HttpErrorResponse) => {
            // Track failed lookup
            this.trackEvent("ID&V", "Step One", "Tenancy & Post Code - Not Found");

            // Show error message to user
            if (this.vm.content) {
               this.error(this.vm.content.step1PropertyNotFoundTitle, this.vm.content.step1PropertyNotFoundBody);
            }

            return of(undefined);
         })
      );
   }

   /**
    * STEP 2: Get List of Tenants for the Property
    *
    * Purpose: Retrieves all tenants associated with the verified tenancy
    * Allows user to select which tenant they are (multi-occupancy properties)
    *
    * Called by: Step 2 component after tenancy has been validated in Step 1
    *
    * API Endpoint: GET /api/tenants?tenancyReference={ref}&assetId={id}
    *
    * Request Parameters:
    * - tenancyReference: From vm.tenancyReference (user input from Step 1)
    * - assetId: From vm.tenancy.assetId (returned from getTenancy() in Step 1)
    *
    * Response (200 OK): Array of Tenant objects containing:
    * - title: Mr, Mrs, Ms, etc.
    * - firstName: Tenant's first name
    * - lastName: Tenant's last name
    * - fullName: Complete name for display
    * - contactId: Unique identifier for this tenant (IMPORTANT: used in Step 3)
    * - blocked: Boolean - whether tenant is blocked from portal access
    * - registered: Boolean - whether tenant already has a portal account
    *
    * Response (204 No Content): No tenants found
    *
    * Behavior:
    * - On success: Returns array of tenants for user to select from
    * - On no content (204): Returns null
    * - Tenant selection is stored in vm.selectedContactId
    *
    * Business Rules:
    * - If tenant.blocked === true: Show "blocked" message, prevent registration
    * - If tenant.registered === true: Show "already registered" message, prevent duplicate registration
    */
   getTenants(): Observable<Tenant[] | null> {
      const url = `${this.accountApiUrl}/tenants?tenancyReference=${this.vm.tenancyReference}&assetId=${this.vm.tenancy.assetId}`;

      return this.http.get<Tenant[]>(url).pipe(
         catchError((error: HttpErrorResponse) => {
            if (error.status === 204) {
               return of(null);
            }
            return throwError(() => error);
         })
      );
   }

   /**
    * STEP 3: Get Available Security Questions for Tenant
    *
    * Purpose: Retrieves which security questions are available for the selected tenant
    * Not all tenants have all questions available - depends on data quality
    *
    * Called by: Step 3 component after tenant has been selected in Step 2
    *
    * API Endpoint: GET /api/questions/{tenancyReference}/{assetId}/{contactId}
    *
    * Request Parameters:
    * - tenancyReference: From vm.tenancyReference
    * - assetId: From vm.tenancy.assetId
    * - contactId: From vm.selectedContactId (user's selection in Step 2)
    *
    * Response (200 OK): Array of question identifiers, e.g.:
    * ["NationalInsuranceNumber", "DateOfBirth", "TenancyStart", "RentCharge"]
    *
    * Response (204 No Content or 404 Not Found): No questions available for this tenant
    *
    * Behavior:
    * - On success: Returns array of available question types
    * - On 404/204: Returns undefined (wizard shows "insufficient questions" error)
    *
    * Question Types:
    * - "NationalInsuranceNumber": Tenant's NI number
    * - "DateOfBirth": Tenant's date of birth
    * - "TenancyStart": When the tenancy began
    * - "RentCharge": Current rent amount and frequency
    *
    * Business Rules:
    * - If insufficient questions available, tenant cannot complete verification
    * - Shows error: vm.content.step3InsufficientQuestionsError...
    */
   getQuestions(): Observable<string[] | undefined> {
      this.resetError();

      const url = `${this.accountApiUrl}/questions/${this.vm.tenancyReference}/${this.vm.tenancy.assetId}/${this.vm.selectedContactId}`;

      return this.http.get<string[]>(url).pipe(
         catchError((error: HttpErrorResponse) => {
            if (error.status === 404 || error.status === 204) {
               console.log(`No questions for tenancy reference ${this.vm.tenancyReference}, returning undefined`);
               return of(undefined);
            }
            return throwError(() => error);
         })
      );
   }

   /**
    * STEP 4: Submit Answers to Security Questions for Verification
    *
    * Purpose: Submits the user's answers to security questions for identity verification
    * This is the final verification step before creating a portal account
    *
    * Called by: Step 4 component when user submits their answers
    *
    * API Endpoint: POST /api/answers
    *
    * Request Body: JSON object containing:
    * {
    *   tenancyReference: string,    // From vm.tenancyReference
    *   assetId: string,              // From vm.tenancy.assetId
    *   contactId: string,            // From vm.selectedContactId
    *   answers: [                    // Array of question/answer pairs
    *     {
    *       question: "NationalInsuranceNumber",
    *       answer: "AB123456C"
    *     },
    *     {
    *       question: "DateOfBirth",
    *       answer: "1990-01-15"      // ISO date format
    *     },
    *     {
    *       question: "TenancyStart",
    *       answer: "2020-06-01"      // ISO date format
    *     },
    *     {
    *       question: "RentCharge",
    *       answer: "850.00|Monthly"  // Format: "amount|frequency"
    *     }
    *   ],
    * }
    *
    * Response (200 OK): IdentificationResult with success isLocked flag and optional lockExpiresAt
    *
    * Behavior:
    * - On success: proceed to enroll user and redirect via resumeUrl
    * - On failure: server tracks attempts and sets isLocked when limit is reached
    * - Rent charge format: "amount|frequency" (e.g., "850.00|Monthly", "195.00|Weekly")
    * - Empty answers sent as empty string
    */
   postAnswers(): Observable<IdentificationResult> {
      // Build request payload
      const payload = {
         tenancyReference: this.vm.tenancyReference,
         assetId: this.vm.tenancy.assetId,
         contactId: this.vm.selectedContactId,
         answers: [
            {
               question: "NationalInsuranceNumber",
               answer: this.vm.answers.nationalInsuranceNumber || "",
            },
            {
               question: "DateOfBirth",
               answer: this.vm.answers.dateOfBirth || "",
            },
            {
               question: "TenancyStart",
               answer: this.vm.answers.tenancyStartDate || "",
            },
            {
               question: "RentCharge",
               answer:
                  this.vm.answers.rentFrequency === "null" || !this.vm.answers.rentCharge
                     ? ""
                     : `${this.vm.answers.rentCharge}|${this.vm.answers.rentFrequency}`,
            },
         ],
      };

      const url = `${this.accountApiUrl}/answers`;
      const headers = new HttpHeaders({ "Content-Type": "application/json" });

      return this.http.post<IdentificationResult>(url, payload, { headers }).pipe(
         catchError((error: HttpErrorResponse) => {
            console.error("Error submitting answers:", error);

            // Show error to user
            if (this.vm.content) {
               this.vm.error = new ErrorMessage(
                  this.vm.content.step3InvalidSubmissionErrorTitle,
                  this.vm.content.step3InvalidSubmissionErrorDescription
               );
            }

            return throwError(() => "Failure");
         })
      );
   }

   /**
    * Helper: Reset Error State
    *
    * Clears any existing error message from the ViewModelService
    * Called at the beginning of each API operation
    */
   resetError(): void {
      this.vm.error = null;
   }

   /**
    * Helper: Set Error State
    *
    * Creates and stores an error message in the ViewModelService
    * Error will be displayed to the user in the UI
    *
    * @param title - Error title/heading
    * @param body - Error message body/details
    */
   public error(title: string, body: string): void {
      this.vm.error = new ErrorMessage(title, body);
   }

   /**
    * Helper: Track Google Analytics Events
    *
    * Sends events to Google Analytics if available
    * Used to track user progress through the wizard
    *
    * @param category - Event category (e.g., "ID&V")
    * @param label - Event label (e.g., "Step One")
    * @param action - Event action (e.g., "Tenancy Found")
    */
   private trackEvent(category: string, label: string, action: string): void {
      if ((window as any).ga) {
         (window as any).ga("send", "event", {
            eventCategory: category,
            eventLabel: label,
            eventAction: action,
         });
      }
   }
}
